import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, CheckCircle } from 'lucide-react';

const SafetySection = () => {
  const { t } = useTranslation();
  
  const checkpoints = [
    t('safety.checkpoint1'),
    t('safety.checkpoint2'),
    t('safety.checkpoint3'),
    t('safety.checkpoint4')
  ];

  return (
    <section id="safety-section" className="py-24 bg-[#0a0a0a] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
              <Shield className="w-4 h-4 text-blue-500" />
              <span className="text-blue-500 text-sm font-medium">{t('safety.badge')}</span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('safety.title')} <span className="text-amber-500">{t('safety.titleHighlight')}</span>?
            </h2>

            <p className="text-gray-300 text-lg mb-8 leading-relaxed">
              {t('safety.description')}
            </p>

            <h3 className="text-xl font-semibold text-white mb-4">{t('safety.selectionCriteriaTitle')}</h3>
            <p className="text-gray-400 mb-6">
              {t('safety.selectionCriteriaSubtitle')}
            </p>

            <div className="space-y-4 mb-8">
              {checkpoints.map((item, index) => (
                <div key={`check-${index}`} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-gray-300 text-lg italic border-l-4 border-amber-500 pl-4">
              {t('safety.conclusion')}
            </p>
          </div>

          {/* Right - Visual */}
          <div className="relative">
            <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/20 rounded-3xl p-8 border border-white/10">
              <h3 className="text-2xl font-bold text-white mb-6 text-center">
                {t('safety.safetyCore')}
              </h3>

              <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                {t('safety.safetyCoreDesc1')}
              </p>

              <p className="text-gray-300 text-sm mb-6 leading-relaxed">
                {t('safety.safetyCoreDesc2')}
              </p>

              <h4 className="text-lg font-semibold text-amber-500 mb-4">{t('safety.goldStandardTitle')}</h4>
              <p className="text-gray-400 text-sm mb-4">
                {t('safety.goldStandardDesc')}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-black/30 rounded-xl p-4 text-center">
                  <p className="text-blue-500 font-bold text-3xl">100%</p>
                  <p className="text-gray-400 text-sm">{t('safety.verifiedDrivers')}</p>
                  <p className="text-gray-500 text-xs mt-2">{t('safety.verifiedDriversDesc')}</p>
                </div>
                <div className="bg-black/30 rounded-xl p-4 text-center">
                  <p className="text-blue-500 font-bold text-3xl">100%</p>
                  <p className="text-gray-400 text-sm">{t('safety.inspectedVehicles')}</p>
                  <p className="text-gray-500 text-xs mt-2">{t('safety.inspectedVehiclesDesc')}</p>
                </div>
              </div>

              <h4 className="text-lg font-semibold text-amber-500 mb-3">{t('safety.commitmentTitle')}</h4>
              <div className="bg-black/30 rounded-xl p-4">
                <p className="text-gray-300 text-sm italic">
                  "{t('safety.commitmentQuote')}"
                </p>
              </div>

              {/* Trust badges */}
              <div className="flex justify-center gap-4 mt-6">
                <div className="bg-green-500/20 border border-green-500/30 rounded-lg px-4 py-2">
                  <span className="text-green-500 text-sm font-medium">{t('safety.fullyInsured')}</span>
                </div>
                <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg px-4 py-2">
                  <span className="text-blue-500 text-sm font-medium">{t('safety.licensed')}</span>
                </div>
                <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg px-4 py-2">
                  <span className="text-amber-500 text-sm font-medium">{t('safety.certified')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SafetySection;
